var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getActiveAccount } from "../utils/get-active-account";
/**
 * Fetches account data from the identity service API
 * @param accountId - The account ID to fetch data for
 * @returns Promise resolving to AccountData
 */
const fetchAccountData = (accountId) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield fetch(`/api/auth/identityAccount?accountId=${encodeURIComponent(accountId)}`);
    if (!response.ok) {
        const errorMessage = `Failed to fetch account data from identity service: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
    }
    const responseData = (yield response.json());
    if (!responseData.data) {
        throw new Error("No account data returned from identity service");
    }
    return responseData.data;
});
/**
 * Custom hook to fetch account data for the active account using React Query
 *
 * Uses React Query for:
 * - Automatic caching and deduplication
 * - Shared state across components
 * - Optimized refetching
 *
 * Query Key: ["identityAccount", accountId]
 *
 * @param accountId - Optional account ID. If not provided, uses getActiveAccount()
 *
 */
export const useAccountData = (accountId) => {
    try {
        useQueryClient();
    }
    catch (err) {
        throw new Error("useAccountData: No QueryClient found. " +
            "Make sure your component is wrapped with QueryClientProvider. " +
            "Example:\n\n" +
            "<QueryClientProvider client={queryClient}>\n" +
            "  <YourComponent />\n" +
            "</QueryClientProvider>");
    }
    // Memoize the account ID to prevent unnecessary re-queries
    const activeAccountId = useMemo(() => accountId !== null && accountId !== void 0 ? accountId : getActiveAccount(), [accountId]);
    const { data: accountData = null, isLoading, error, refetch, } = useQuery({
        queryKey: ["identityAccount", activeAccountId],
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        queryFn: () => fetchAccountData(activeAccountId),
        enabled: Boolean(activeAccountId && activeAccountId !== ""),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
    });
    return {
        accountData,
        isLoading,
        error,
        refetch: () => {
            void refetch();
        },
    };
};
